// In-dial (per-instance) roles — mirrors the Domain InstanceRole enum. The single source both web
// apps build role pickers, chips and descriptions from, so the names and wording can't drift between
// the apps again (KEY-296).

export interface InstanceRoleMeta {
  /** The API value, e.g. "Editor" — matches the Domain InstanceRole member names. */
  name: string;
  /** One-line description of what the role can do, shown in pickers and help. */
  description: string;
}

export const INSTANCE_ROLES: readonly InstanceRoleMeta[] = [
  { name: "Admin", description: "Manages the dial and its members." },
  { name: "Editor", description: "Records and changes status." },
  { name: "Reviewer", description: "Reviews and signs off." },
  { name: "Reader", description: "View-only." },
];

export const INSTANCE_ROLE_NAMES: readonly string[] = INSTANCE_ROLES.map((r) => r.name);

export function instanceRoleDescription(name: string): string | undefined {
  return INSTANCE_ROLES.find((r) => r.name === name)?.description;
}

/** One configurable instance action of the role-action matrix (KEY-298). */
export interface InstanceActionMeta {
  /** The API value — matches the Domain InstanceAction member names. */
  name: string;
  label: string;
  description: string;
  /** Set only on actions carrying a SECOND mask (KEY-285: who a restricted sender may address). The
   * permission UIs render it as a sub-line under the action's own chips — one row, two questions,
   * rather than a second identical strip nobody could tell apart from the first. */
  targetLabel?: string;
  /** What the target sub-line means when no roles are picked. */
  targetAnyLabel?: string;
}

/** The configurable actions, in display order. Status changes are deliberately absent — they have their
 * own richer per-status configuration. One source for both apps, like INSTANCE_ROLES above.
 *
 * The last two are READS (KEY-537), and the only ones here that are. Everything above them refuses an
 * action and says so; a read mask set too narrow instead makes evidence quietly disappear — nobody is told,
 * least of all the person who filed it. The descriptions say so in as many words, because the person
 * setting them is choosing between "they can't change this" and "they can't see this ever happened". */
export const INSTANCE_ACTIONS: readonly InstanceActionMeta[] = [
  { name: "AttributeEdit", label: "Edit fields", description: "Fill in and change the information fields on sections, segments and items." },
  { name: "DocumentUpload", label: "Upload documents", description: "Add and confirm evidence files." },
  { name: "DocumentVersions", label: "Manage document versions", description: "Delete document versions and roll back to earlier ones." },
  { name: "MessagePost", label: "Post public messages", description: "Write in the discussions on segments and items, where everyone on the action point can read it." },
  {
    name: "MessagePostRestricted",
    label: "Post restricted messages",
    description: "Write to named people only. Dial admins and Key Dials support can also read restricted messages.",
    targetLabel: "…and may address",
    targetAnyLabel: "Anyone who can see the action point",
  },
  {
    name: "DocumentList",
    label: "See the file list",
    description: "See that files exist at all. Anyone left out finds an empty Files tab — they aren't told anything is being kept from them, because a file's NAME can give away the very thing it was hidden for.",
  },
  {
    name: "DocumentDownload",
    label: "Download files",
    description: "Open and save a file's contents. Anyone left out still sees the file listed — they know it's there, just not what's in it.",
  },
];
